import { useId } from 'react';
import {
    DAY_TOKENS,
    DAY_TOKEN_LABELS,
    TIME_OPTIONS,
    allDaysClaimed,
    availabilityRangeKey,
    createEmptyScheduleBlock,
    daysClaimedByOtherEntries,
    DEFAULT_SCHEDULE_BLOCK,
    findOverlappingRanges,
    isOvernightBlock,
    normalizeAvailability,
    normalizeScheduleArray,
} from '../../utils/availabilitySchedule';
import './ScheduleBlockEditor.css';

/**
 * Controlled availability editor with optional 24/7 all-day mode.
 * @param {{ value: { allDay: boolean, schedule: Array }, onChange: (value: object) => void, readOnly?: boolean, defaultNote?: string }} props
 */
export default function ScheduleBlockEditor({
    value,
    onChange,
    readOnly = false,
    defaultNote = 'Defaults to 9:00 PM – 5:00 AM every day. Adjust as needed.',
}) {
    const toggleId = useId();
    const { allDay, schedule } = normalizeAvailability(value);
    const blocks = schedule;
    const overlappingKeys = findOverlappingRanges({ allDay, schedule: blocks });

    // Hoist so `toggleDay` can reuse the claim set without rebuilding it per chip.
    const claimedByEntry = blocks.map((_, entryIndex) => (
        daysClaimedByOtherEntries({ allDay, schedule: blocks }, entryIndex)
    ));

    const emitChange = (nextAllDay, nextSchedule) => {
        if (readOnly) return;
        onChange({
            allDay: nextAllDay,
            schedule: normalizeScheduleArray(nextSchedule),
        });
    };

    const updateSchedule = (nextSchedule) => {
        emitChange(allDay, nextSchedule);
    };

    const handleToggleAllDay = (checked) => {
        if (checked) {
            emitChange(true, blocks);
            return;
        }
        const nextSchedule = blocks.length > 0
            ? blocks
            : normalizeScheduleArray([DEFAULT_SCHEDULE_BLOCK]);
        emitChange(false, nextSchedule);
    };

    const updateEntry = (entryIndex, patch) => {
        updateSchedule(blocks.map((entry, i) => (i === entryIndex ? { ...entry, ...patch } : entry)));
    };

    const updateBlockRange = (entryIndex, blockIndex, patch) => {
        updateEntry(entryIndex, {
            blocks: blocks[entryIndex].blocks.map((block, i) => (
                i === blockIndex ? { ...block, ...patch } : block
            )),
        });
    };

    const toggleDay = (entryIndex, day) => {
        const entry = blocks[entryIndex];
        const isCurrentlyChecked = entry.days.includes(day);
        // Refuse to add a day that's already claimed by a sibling block.
        // The chip's `disabled` attribute is the primary guard, but a
        // synthetic click or programmatic onChange could bypass it —
        // defend in depth here so the state never holds a duplicate day
        // across blocks.
        if (!isCurrentlyChecked) {
            // claimedByEntry is .map'd from `blocks`, so this is always defined.
            if (claimedByEntry[entryIndex].has(day)) return;
        }
        const days = isCurrentlyChecked
            ? entry.days.filter((d) => d !== day)
            : [...entry.days, day];
        updateEntry(entryIndex, { days });
    };

    const addScheduleBlock = () => {
        // Hard cap in the handler so the 7-block ceiling holds even if
        // the render-time guard (which hides the button) is ever
        // bypassed (test renderer, refactored layout, programmatic call).
        if (blocks.length >= DAY_TOKENS.length) return;
        updateSchedule([...blocks, createEmptyScheduleBlock()]);
    };

    const removeScheduleBlock = (entryIndex) => {
        updateSchedule(blocks.filter((_, i) => i !== entryIndex));
    };

    const addTimeRange = (entryIndex) => {
        const entry = blocks[entryIndex];
        updateEntry(entryIndex, {
            blocks: [...entry.blocks, { start: '09:00', end: '17:00' }],
        });
    };

    const removeTimeRange = (entryIndex, blockIndex) => {
        const entry = blocks[entryIndex];
        const nextBlocks = entry.blocks.filter((_, i) => i !== blockIndex);
        if (nextBlocks.length === 0) {
            removeScheduleBlock(entryIndex);
            return;
        }
        updateEntry(entryIndex, { blocks: nextBlocks });
    };

    return (
        <div className="schedule-block-editor avail-sched">
            {!allDay && defaultNote && (
                <div className="avail-sched-default-note">{defaultNote}</div>
            )}

            <div className="avail-allday-row">
                <label className="avail-allday-label" htmlFor={toggleId}>
                    Available 24 Hours
                    <span className="sub">
                        Your parking area is available 24 hours a day, 7 days a week. 
                    </span>
                </label>
                <label className="avail-toggle" htmlFor={toggleId}>
                    <input
                        id={toggleId}
                        type="checkbox"
                        role="switch"
                        checked={allDay}
                        disabled={readOnly}
                        onChange={(e) => handleToggleAllDay(e.target.checked)}
                    />
                    <span className="track" aria-hidden="true" />
                    <span className="knob" aria-hidden="true" />
                </label>
            </div>

            {allDay && readOnly && (
                <p className="text-sm text-gray-500">Open 24/7.</p>
            )}

            {!allDay && blocks.length === 0 && readOnly && (
                <p className="text-sm text-gray-500">No availability schedule set.</p>
            )}

            {!allDay && blocks.map((entry, entryIndex) => (
                <div key={`schedule-${entryIndex}`} className="avail-block">
                    <div className="avail-block-head">
                        <div className="avail-block-title">
                            Schedule block {entryIndex + 1}
                        </div>
                        {!readOnly && blocks.length > 1 && (
                            <button
                                type="button"
                                className="avail-block-remove"
                                onClick={() => removeScheduleBlock(entryIndex)}
                            >
                                Remove block
                            </button>
                        )}
                    </div>

                    {entry.blocks.map((block, blockIndex) => (
                        <div key={`range-${entryIndex}-${blockIndex}`} className="avail-range">
                            <div className="avail-range-grid">
                                <div>
                                    <label className="avail-field-label" htmlFor={`avail-start-${entryIndex}-${blockIndex}`}>
                                        Start
                                    </label>
                                    <select
                                        id={`avail-start-${entryIndex}-${blockIndex}`}
                                        className="avail-select"
                                        value={block.start}
                                        disabled={readOnly}
                                        onChange={(e) => updateBlockRange(entryIndex, blockIndex, { start: e.target.value })}
                                    >
                                        {TIME_OPTIONS.map((opt) => (
                                            <option key={`start-${opt.value}`} value={opt.value}>
                                                {opt.display}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="avail-field-label" htmlFor={`avail-end-${entryIndex}-${blockIndex}`}>
                                        End
                                    </label>
                                    <select
                                        id={`avail-end-${entryIndex}-${blockIndex}`}
                                        className="avail-select"
                                        value={block.end}
                                        disabled={readOnly}
                                        onChange={(e) => updateBlockRange(entryIndex, blockIndex, { end: e.target.value })}
                                    >
                                        {TIME_OPTIONS.map((opt) => (
                                            <option key={`end-${opt.value}`} value={opt.value}>
                                                {opt.display}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {!readOnly && entry.blocks.length > 1 ? (
                                    <button
                                        type="button"
                                        className="avail-range-remove"
                                        title="Remove time range"
                                        onClick={() => removeTimeRange(entryIndex, blockIndex)}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                                            <line x1="5" y1="12" x2="19" y2="12" />
                                        </svg>
                                    </button>
                                ) : (
                                    <span />
                                )}
                            </div>
                            {isOvernightBlock(block) && (
                                <div className="avail-overnight-note">Ends next day (overnight range).</div>
                            )}
                            {overlappingKeys.has(availabilityRangeKey(entryIndex, blockIndex)) && (
                                <div className="avail-overlap-note">
                                    Overlaps another time block — adjust the times.
                                </div>
                            )}
                        </div>
                    ))}

                    {!readOnly && (
                        <button
                            type="button"
                            className="avail-add-range"
                            onClick={() => addTimeRange(entryIndex)}
                        >
                            + Add time range
                        </button>
                    )}

                    <div className="avail-days-label">Days</div>
                    <div className="avail-days">
                        {DAY_TOKENS.map((day) => {
                            const checked = entry.days.includes(day);
                            const claimed = !checked && claimedByEntry[entryIndex].has(day);
                            const disabled = readOnly || claimed;
                            let className = 'avail-day-chip';
                            if (checked) className += ' on';
                            if (readOnly) className += ' read-only';
                            if (claimed) className += ' claimed';
                            return (
                                <label
                                    key={`${entryIndex}-${day}`}
                                    className={className}
                                    title={claimed ? 'Already used in another schedule block' : undefined}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={disabled}
                                        onChange={() => toggleDay(entryIndex, day)}
                                    />
                                    {DAY_TOKEN_LABELS[day]}
                                </label>
                            );
                        })}
                    </div>
                </div>
            ))}

            {/* blocks.length cap covers the "8th block added before any
                days picked" transient; allDaysClaimed is the steady-state guard. */}
            {!readOnly && !allDay && blocks.length < DAY_TOKENS.length
                && !allDaysClaimed({ allDay, schedule: blocks }) && (
                <button
                    type="button"
                    className="avail-add-block"
                    onClick={addScheduleBlock}
                >
                    + Add schedule block
                </button>
            )}
        </div>
    );
}
